import type {
  ParsedInvoiceBandAuditView,
  ParsedInvoiceReport,
  ParsedInvoiceTotalsAuditView,
} from "../api/types";

/**
 * G-66: read the parser's report out of `Invoice.parsedPayload`.
 *
 * The payload is typed `unknown` on purpose — it is whatever the parser that
 * handled this supplier stored, and its shape has changed across parser
 * versions. So every field is checked rather than cast: an invoice parsed by
 * an older version simply reports nothing instead of crashing the page.
 */
export function readParsedInvoiceReport(payload: unknown): ParsedInvoiceReport {
  if (!isRecord(payload)) return { warnings: [], totalsAudit: null };

  const warnings = Array.isArray(payload.warnings)
    ? payload.warnings.filter((w): w is string => typeof w === "string")
    : [];

  return { warnings, totalsAudit: readTotalsAudit(payload.totalsAudit) };
}

function readTotalsAudit(raw: unknown): ParsedInvoiceTotalsAuditView | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.linesSumEur !== "string") return null;

  const bands = Array.isArray(raw.bands)
    ? raw.bands
        .map(readBandAudit)
        .filter((b): b is ParsedInvoiceBandAuditView => b !== null)
    : undefined;

  return {
    linesSumEur: raw.linesSumEur,
    documentTotalEur:
      typeof raw.documentTotalEur === "string" ? raw.documentTotalEur : null,
    grandTotalEur:
      typeof raw.grandTotalEur === "string" ? raw.grandTotalEur : null,
    // G-121 S1: only the two spellings the parser writes. Anything else — an
    // older payload that never stored it, or a value this build does not
    // know — reads as "the payload did not say", and the card then names the
    // figure as "stated total" rather than guessing base or gross.
    documentTotalKind:
      raw.documentTotalKind === "BASE" || raw.documentTotalKind === "GROSS"
        ? raw.documentTotalKind
        : null,
    // NOT `!== false`. A payload that never stored the flag — an invoice
    // parsed by an older version — would then read as TRUE, and the card
    // would print "they agree" about a comparison that never ran. The whole
    // point of showing these checks is that an unchecked document must not
    // look checked, so an absent boolean stays null and renders as unknown.
    totalsAligned:
      typeof raw.totalsAligned === "boolean" ? raw.totalsAligned : null,
    bands: bands && bands.length > 0 ? bands : undefined,
  };
}

function readBandAudit(raw: unknown): ParsedInvoiceBandAuditView | null {
  if (!isRecord(raw)) return null;
  if (
    typeof raw.ivaCode !== "string" ||
    typeof raw.documentBaseEur !== "string" ||
    typeof raw.linesSumEur !== "string"
  ) {
    return null;
  }
  return {
    ivaCode: raw.ivaCode,
    documentBaseEur: raw.documentBaseEur,
    linesSumEur: raw.linesSumEur,
    aligned: typeof raw.aligned === "boolean" ? raw.aligned : null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
